import React, { useState } from "react";
import { Box, Typography, SvgIcon, Collapse } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { formatCurrency, formatPercent } from "../../../../../helpers";
import Tooltip from "../../../../../components/Tooltip";
import {
  INBOUND_PLACEMENT_TYPES,
  INBOUND_PLACEMENT_TOOLTIP,
} from "../../../../../constants/productAnalyzer";
import NumberInput from "../../common/NumberInput";
import CollapsibleSection from "./components/CollapsibleSection";
import FeeRow from "./components/FeeRow";
import StaticRow from "./components/StaticRow";
import EditablePercentFeeRow from "./components/EditablePercentFeeRow";
import { ProfitCalculatorProps } from "./types";

export default function ProfitCalculator({
  calculationData,
  onQuantityChange,
  onInboundPlacementChange,
  onPrepFeeChange,
  onInboundShippingChange,
  onMiscFeeChange,
  onMiscFeePercentChange,
}: ProfitCalculatorProps) {
  const [totalFeesIsOpen, setTotalFeesIsOpen] = useState(false);
  const [additionalExpensesIsOpen, setAdditionalExpensesIsOpen] =
    useState(false);
  const [amazonPayoutIsOpen, setAmazonPayoutIsOpen] = useState(false);
  const [quantityIsOpen, setQuantityIsOpen] = useState(false);

  const {
    totalFee,
    additionalExpenses,
    discountPercent,
    breakevenPrice,
    amazonPayout,
    quantity,
    referralFee,
    referralPercent,
    fbaFee,
    inboundPlacementFee,
    storageFee,
    inboundShipping,
    prepFee,
    miscFeeAmount,
    miscFeePercent,
    miscFeePercentAmount,
    salePrice,
    fulfilmentFee,
    closingFee,
    totalCost,
    totalSale,
    totalProfit,
    fulfilmentType,
    inboundPlacementService,
  } = calculationData;

  const isFBM = fulfilmentType === "FBM";

  return (
    <Box>
      {/* Total Fees Section */}
      <CollapsibleSection
        title="Total Fees"
        value={formatCurrency(totalFee)}
        isOpen={totalFeesIsOpen}
        onToggle={() => setTotalFeesIsOpen((prev) => !prev)}
      >
        <FeeRow
          label={`Referral Fee (${referralPercent}%)`}
          value={formatCurrency(referralFee)}
        />
        <FeeRow
          label={`${fulfilmentType} Fee`}
          value={formatCurrency(fulfilmentType === "FBA" ? fbaFee : 0)}
        />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            py: 0.125,
            minHeight: "20px",
            pr: "20px",
          }}
        >
          <Tooltip
            title={
              <Box
                dangerouslySetInnerHTML={{
                  __html: INBOUND_PLACEMENT_TOOLTIP[inboundPlacementService],
                }}
              />
            }
          >
            <Typography
              fontSize="12px"
              fontWeight={400}
              color="#555555"
              lineHeight="18px"
              sx={{
                cursor: fulfilmentType === "FBA" ? "pointer" : "default",
                textDecorationLine: isFBM
                  ? "line-through"
                  : fulfilmentType === "FBA"
                    ? "underline"
                    : "none",
                textDecorationStyle:
                  fulfilmentType === "FBA" ? "dotted" : "solid",
                textDecorationColor: "#202020",
                textUnderlineOffset: "2px",
              }}
              onClick={() => {
                if (fulfilmentType === "FBA") {
                  const index = INBOUND_PLACEMENT_TYPES.indexOf(
                    inboundPlacementService,
                  );
                  onInboundPlacementChange(
                    INBOUND_PLACEMENT_TYPES[
                      (index + 1) % INBOUND_PLACEMENT_TYPES.length
                    ],
                  );
                }
              }}
            >
              Inbound Placement ({inboundPlacementService.slice(0, 1)})
            </Typography>
          </Tooltip>
          <Typography
            fontSize="12px"
            fontWeight={400}
            color="#555555"
            lineHeight="18px"
            sx={{
              textDecorationLine: isFBM ? "line-through" : "none",
            }}
          >
            {formatCurrency(inboundPlacementFee)}
          </Typography>
        </Box>
        <FeeRow
          label="Storage Fee"
          value={formatCurrency(storageFee)}
          strikethrough={isFBM}
        />
      </CollapsibleSection>

      {/* Additional Expenses Section */}
      <CollapsibleSection
        title="Additional Expenses"
        value={formatCurrency(additionalExpenses)}
        isOpen={additionalExpensesIsOpen}
        onToggle={() => setAdditionalExpensesIsOpen((prev) => !prev)}
      >
        <FeeRow
          label="Inbound Shipping"
          value={formatCurrency(inboundShipping)}
          editable={isFBM ? false : true}
          onChange={onInboundShippingChange}
          strikethrough={isFBM}
        />
        <FeeRow
          label="Prep Fee"
          value={formatCurrency(prepFee)}
          editable={isFBM ? false : true}
          onChange={onPrepFeeChange}
          strikethrough={isFBM}
        />
        <FeeRow
          label="Misc Fee"
          value={formatCurrency(miscFeeAmount)}
          editable
          onChange={onMiscFeeChange}
        />
        <EditablePercentFeeRow
          labelPrefix="Misc Fee ("
          labelSuffix="% of cost)"
          percentValue={miscFeePercent}
          displayValue={formatCurrency(miscFeePercentAmount)}
          onPercentChange={onMiscFeePercentChange}
        />
      </CollapsibleSection>

      {/* Static Rows */}
      <StaticRow
        label="Discount"
        value={formatPercent(discountPercent / 100)}
      />
      <StaticRow
        label="Breakeven Price"
        value={formatCurrency(breakevenPrice)}
      />

      {/* Amazon Payout Section */}
      <CollapsibleSection
        title="Amazon Payout"
        value={formatCurrency(amazonPayout)}
        isOpen={amazonPayoutIsOpen}
        onToggle={() => setAmazonPayoutIsOpen((prev) => !prev)}
      >
        <FeeRow label="Sale Price" value={formatCurrency(salePrice)} />
        <FeeRow label="Referral Fee" value={formatCurrency(referralFee)} />
        <FeeRow label="Fulfilment Fee" value={formatCurrency(fulfilmentFee)} />
        <FeeRow label="Closing Fee" value={formatCurrency(closingFee)} />
      </CollapsibleSection>

      {/* Quantity Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          py: 0.375,
          minHeight: "26px",
        }}
      >
        <Typography
          fontSize="12px"
          fontWeight={500}
          color="#0F1111"
          lineHeight="20px"
          sx={{ cursor: "pointer" }}
          onClick={() => setQuantityIsOpen((prev) => !prev)}
        >
          Quantity
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <NumberInput value={quantity} onChange={onQuantityChange} min={1} />
          <SvgIcon
            component={KeyboardArrowDownIcon}
            sx={{
              fontSize: "16px",
              color: "#00576B",
              transition: "transform 0.25s ease",
              transform: quantityIsOpen ? "rotate(180deg)" : "rotate(0deg)",
              cursor: "pointer",
            }}
            onClick={() => setQuantityIsOpen((prev) => !prev)}
          />
        </Box>
      </Box>
      <Collapse
        in={quantityIsOpen}
        sx={{
          "& .MuiCollapse-wrapperInner": {
            pt: 0.25,
            pb: 0.5,
          },
        }}
      >
        <FeeRow label="Cost" value={formatCurrency(totalCost)} />
        <FeeRow label="Sale" value={formatCurrency(totalSale)} />
        <FeeRow label="Total Profit" value={formatCurrency(totalProfit)} />
      </Collapse>
    </Box>
  );
}
